/**
 * One hook that owns a full-bleed canvas: HiDPI sizing, a rAF loop, and
 * pointer plumbing that survives multi-touch and does not let the page scroll.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import type { Vec } from '../engine/vec.ts';
import { unlockAudio } from '../engine/audio.ts';

export interface Size {
  width: number;
  height: number;
}

export interface SurfacePointer {
  id: number;
  pos: Vec;
  /** Where this pointer started — lets a mode tell a tap from a drag. */
  start: Vec;
  startedAt: number;
  pressure: number;
}

export interface SurfaceWheel {
  /** Where the cursor was, in canvas coordinates. */
  pos: Vec;
  /** Positive scrolls away from the reader. */
  deltaY: number;
  /** True for a trackpad pinch, which browsers report as a ctrl-wheel. */
  pinch: boolean;
}

export interface SurfaceHandlers {
  onFrame?: (ctx: CanvasRenderingContext2D, dt: number, size: Size) => void;
  onDown?: (p: SurfacePointer, size: Size) => void;
  onMove?: (p: SurfacePointer, size: Size) => void;
  onUp?: (p: SurfacePointer, size: Size) => void;
  onResize?: (size: Size) => void;
  /** Only wired up by surfaces that pan and zoom. */
  onWheel?: (w: SurfaceWheel, size: Size) => void;
}

const MAX_DPR = 2;

export function useSurface(canvasRef: RefObject<HTMLCanvasElement>, handlers: SurfaceHandlers): Size {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const sizeRef = useRef<Size>({ width: 0, height: 0 });
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Size the backing store to the element box at capped device pixel ratio.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const apply = () => {
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      const bw = Math.round(width * dpr);
      const bh = Math.round(height * dpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (sizeRef.current.width !== width || sizeRef.current.height !== height) {
        sizeRef.current = { width, height };
        setSize({ width, height });
        handlersRef.current.onResize?.({ width, height });
      }
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(canvas);
    window.addEventListener('orientationchange', apply);
    window.visualViewport?.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', apply);
      window.visualViewport?.removeEventListener('resize', apply);
    };
  }, [canvasRef]);

  // Animation loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const ctx = canvas.getContext('2d');
      if (!ctx || sizeRef.current.width === 0) return;
      handlersRef.current.onFrame?.(ctx, dt, sizeRef.current);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef]);

  // Pointers.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = new Map<number, SurfacePointer>();

    const toLocal = (e: PointerEvent): Vec => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const down = (e: PointerEvent) => {
      if (e.button !== undefined && e.button > 0) return;
      e.preventDefault();
      unlockAudio();
      canvas.setPointerCapture(e.pointerId);
      const pos = toLocal(e);
      const p: SurfacePointer = {
        id: e.pointerId,
        pos,
        start: { ...pos },
        startedAt: performance.now(),
        pressure: e.pressure > 0 ? e.pressure : 0.5,
      };
      active.set(e.pointerId, p);
      handlersRef.current.onDown?.(p, sizeRef.current);
    };

    const move = (e: PointerEvent) => {
      const p = active.get(e.pointerId);
      if (!p) return;
      e.preventDefault();
      // Coalesced events keep fast strokes smooth on high-rate touchscreens.
      const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
      for (const ce of events.length ? events : [e]) {
        p.pos = toLocal(ce as PointerEvent);
        p.pressure = ce.pressure > 0 ? ce.pressure : p.pressure;
        handlersRef.current.onMove?.(p, sizeRef.current);
      }
    };

    const up = (e: PointerEvent) => {
      const p = active.get(e.pointerId);
      if (!p) return;
      e.preventDefault();
      p.pos = toLocal(e);
      active.delete(e.pointerId);
      handlersRef.current.onUp?.(p, sizeRef.current);
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };

    const wheel = (e: WheelEvent) => {
      if (!handlersRef.current.onWheel) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      handlersRef.current.onWheel(
        {
          pos: { x: e.clientX - rect.left, y: e.clientY - rect.top },
          deltaY: e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY,
          pinch: e.ctrlKey,
        },
        sizeRef.current
      );
    };

    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', wheel, { passive: false });
    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      canvas.removeEventListener('wheel', wheel);
    };
  }, [canvasRef]);

  return size;
}

/** A press counts as a tap if it was short and barely moved. */
export function isTap(p: SurfacePointer): boolean {
  const moved = Math.hypot(p.pos.x - p.start.x, p.pos.y - p.start.y);
  return moved < 12 && performance.now() - p.startedAt < 350;
}
